import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  FlatList,
  Platform,
  RefreshControl,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import {useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import ProductsService from '../../services/ProductsService/ProductsService';
import HomeSectionsService from '../../services/HomeSectionsService';
import {useBagStore} from '../../store/BagStore';
import {useUserStore} from '../../store/UserStore';
import {useFilterStore} from '../../store/FilterStore';
import ProductList from '../Home/components/ProductList/ProductList';
import ModalFilter from '../Home/components/ModalFilter/ModalFilter';
import {calculatePriceBasedOnQuantity} from '../../utils/price';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import NotFound from '../Menu/components/NotFound/NotFound';
import {AppRoutes} from '../../navigation/Routes.types';
import BagModal from '../Home/components/BagModal/BagModal';
import BrandPills from './BrandPills';
import CategoryPills from './CategoryPills';
import HomeInput from '../../components/HomeInput/HomeInput';
import {IBrand, ISegment} from '../../types/Filter';

const PER_PAGE = 30;

const CatalogBrowseScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const params = (route.params as any) ?? {};
  const filter = params?.filter ?? {};

  const lockedCategory: boolean = filter.lockedCategory === true;

  const title: string = filter.searchQuery
    ? '🔎 BUSCA'
    : filter.title
    ? filter.title.toUpperCase()
    : filter.source === 'suggested'
    ? '📋 PEDIDO SUGERIDO'
    : '📦 CATÁLOGO';

  const categoryIds: number[] | undefined =
    filter.categoryIds?.length > 0 ? filter.categoryIds : undefined;

  const categoryIdsHeader = categoryIds ? categoryIds.join(',') : undefined;

  const categoryNamesFromFilter: string[] | undefined =
    filter.categoryNames?.length > 0 ? filter.categoryNames : undefined;

  const categoryNameHeader = categoryNamesFromFilter
    ? categoryNamesFromFilter.join(',')
    : undefined;

  const carouselId: number | undefined = filter.carouselId ?? undefined;
  // Pedido Sugerido "Ver todos" — flat curated list, no carouselId. Treated like
  // a personal carousel in the UI (brands from products, no category pills/search).
  const isSuggested: boolean = filter.source === 'suggested';
  // Personal carousels (last_ordered, favorites, most_ordered) have a carouselId
  // but no category names — brands derived from actual products, no category pills.
  const isPersonalCarousel: boolean =
    (carouselId !== undefined && !categoryNamesFromFilter?.length) || isSuggested;

  const {user} = useUserStore();
  const {addProduct, updateProductQuantity} = useBagStore();
  const bagProducts = useBagStore(state => state.products);
  const filterStore = useFilterStore();

  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterModal, setFilterModal] = useState(false);
  const [packagingFilter, setPackagingFilter] = useState<any[]>([]);

  // Multi-select brand filter
  // brandNames: marca pré-selecionada (redirect de banner).
  const [selectedBrands, setSelectedBrands] = useState<string[]>(
    filter.brandNames?.length > 0 ? filter.brandNames : [],
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSort, setSelectedSort] = useState<string | null>(null);
  const [selectedPackaging, setSelectedPackaging] = useState<string | null>(null);
  // searchQuery drives the fetch; searchInput is the immediate text in the field.
  const [searchQuery, setSearchQuery] = useState(filter.searchQuery ?? '');
  const [searchInput, setSearchInput] = useState(filter.searchQuery ?? '');
  const searchDebounce = useRef<NodeJS.Timeout | null>(null);

  // Busca-enquanto-digita com debounce (~400ms) na própria tela de catálogo.
  const handleSearchInput = (text: string) => {
    setSearchInput(text);
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }
    searchDebounce.current = setTimeout(() => {
      setSearchQuery(text.trim());
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounce.current) {
        clearTimeout(searchDebounce.current);
      }
    };
  }, []);

  const [allBrands, setAllBrands] = useState<IBrand[]>([]);
  const [visibleBrands, setVisibleBrands] = useState<IBrand[]>([]);
  // Brands extracted from the actual products (used for personal carousels)
  const [carouselBrands, setCarouselBrands] = useState<IBrand[]>([]);
  const categories: ISegment[] = filterStore.category;

  const {width: windowWidth} = useWindowDimensions();
  const numColumns = useMemo(() => {
    if (Platform.OS !== 'web') return 2;
    if (windowWidth >= 1300) return 5;
    if (windowWidth >= 1000) return 4;
    if (windowWidth >= 750) return 3;
    return 2;
  }, [windowWidth]);

  const bagProductsRef = useRef(bagProducts);
  useEffect(() => {
    bagProductsRef.current = bagProducts;
  }, [bagProducts]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setSelectedBrands([]);
        setSelectedCategory(null);
        setSelectedSort(null);
        setSelectedPackaging(null);
        setSearchQuery('');
        setSearchInput('');
        setCarouselBrands([]);
      };
    }, []),
  );

  // Load global brand list once — needed for both category AND personal carousels
  // (personal carousels use it to match product marca against brand logos).
  useEffect(() => {
    const loadBrands = async () => {
      let fetched = filterStore.brand;
      if (!fetched || fetched.length === 0) {
        try {
          fetched = await ProductsService.GetBrands();
          filterStore.setBrand(fetched);
        } catch (e) {
          console.warn('[BrandPills] failed to load global brand list:', e);
          fetched = [];
        }
      }
      setAllBrands(fetched);
    };
    loadBrands();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A marca vinda de fora pode ter outra caixa ("AMSTEL" x "Amstel"); alinha
  // com o nome da pill para ela aparecer selecionada e poder ser desmarcada.
  useEffect(() => {
    if (allBrands.length === 0) return;
    setSelectedBrands(prev => {
      const next = prev.map(
        name =>
          allBrands.find(b => b.name.toLowerCase() === name.toLowerCase())
            ?.name ?? name,
      );
      return next.every((name, i) => name === prev[i]) ? prev : next;
    });
  }, [allBrands]);

  // Compute visible brands for category-based carousels
  useEffect(() => {
    if (isPersonalCarousel) return;
    if (lockedCategory) {
      if (categoryIds?.length) {
        const result = allBrands.filter((b: IBrand) => categoryIds.includes(b.image_segmentation_id));
        setVisibleBrands(result);
      } else if (categoryNamesFromFilter?.length) {
        const matchedIds = filterStore.category
          .filter(c => categoryNamesFromFilter.some(n => n.toLowerCase() === c.name.toLowerCase()))
          .map((c: any) => c.id)
          .filter(Boolean);
        const result =
          matchedIds.length > 0
            ? allBrands.filter((b: IBrand) => matchedIds.includes(b.image_segmentation_id))
            : allBrands;
        setVisibleBrands(result);
      } else {
        setVisibleBrands([]);
      }
    } else if (selectedCategory) {
      const matchedIds = filterStore.category
        .filter(c => c.name.toLowerCase() === selectedCategory.toLowerCase())
        .map((c: any) => c.id)
        .filter(Boolean);
      const result =
        matchedIds.length > 0
          ? allBrands.filter((b: IBrand) => matchedIds.includes(b.image_segmentation_id))
          : [];
      setVisibleBrands(result);
    } else {
      setVisibleBrands([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBrands, selectedCategory, isPersonalCarousel]);

  // Build brand pills from the products actually present in the carousel
  const buildCarouselBrands = useCallback(
    (prods: IProduct[], existingBrands: IBrand[]) => {
      if (!isPersonalCarousel) return;

      const allGlobal = existingBrands.length > 0 ? existingBrands : filterStore.brand ?? [];

      // Collect unique marca names from products (field comes lowercase from API)
      const rawNames = prods
        .map(p => (p as any).marca ?? (p as any).MARCA ?? '')
        .filter(Boolean) as string[];
      const namesInCarousel = new Set(rawNames);

      console.log('[BrandPills] buildCarouselBrands', {
        carouselId,
        productCount: prods.length,
        rawBrandNames: Array.from(namesInCarousel),
        globalBrandListCount: allGlobal.length,
        globalBrandSample: allGlobal.slice(0, 5).map(b => b.name),
      });

      if (namesInCarousel.size === 0) {
        console.warn('[BrandPills] no marca field found in any product — pills will be empty');
        return;
      }

      // Case-insensitive match against global brand list to get logo URLs
      const lowerNamesInCarousel = new Set(Array.from(namesInCarousel).map(n => n.toLowerCase()));
      const matched = allGlobal.filter(b => lowerNamesInCarousel.has(b.name.toLowerCase()));

      console.log('[BrandPills] matched brands with logo:', matched.length, 'of', namesInCarousel.size);

      if (matched.length > 0) {
        setCarouselBrands(matched);
      } else {
        // Fallback: build pills from raw names without logos, title-cased
        const toTitleCase = (s: string) =>
          s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        const fallback = Array.from(namesInCarousel).map(name => ({
          name: toTitleCase(name),
          url: '',
          image_segmentation_id: 0,
          id: 0,
        }));
        console.warn('[BrandPills] no logo match found — using fallback pills (no logo):', fallback.map(b => b.name));
        setCarouselBrands(fallback);
      }
    },
    [isPersonalCarousel, filterStore, carouselId],
  );

  const fetchProducts = useCallback(
    async (page: number, append = false) => {
      if (!user?.clientCode) return;
      append ? setLoadingMore(true) : setLoading(true);
      try {
        let fetched: IProduct[] = [];
        let moreAvailable = false;

        if (isSuggested) {
          const data = await HomeSectionsService.getSuggestedAllProducts(
            user.clientCode,
            PER_PAGE,
            page,
            selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
            selectedPackaging ?? undefined,
          );
          fetched = (data?.products ?? []) as IProduct[];
          const total: number = data?.total ?? 0;
          moreAvailable = page < Math.ceil(total / PER_PAGE);
        } else if (carouselId !== undefined) {
          const data = await HomeSectionsService.getCarouselAllProducts(
            user.clientCode,
            carouselId,
            PER_PAGE,
            page,
            selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
            selectedPackaging ?? undefined,
          );
          fetched = (data?.products ?? []) as IProduct[];
          const total: number = data?.total ?? 0;
          moreAvailable = page < Math.ceil(total / PER_PAGE);
          console.log('[CatalogBrowse] carousel fetch', {
            carouselId,
            page,
            fetchedCount: fetched.length,
            total,
            brandFilter: selectedBrands,
            marcasNoPrimeiroProduto: fetched[0] ? (fetched[0] as any).marca ?? (fetched[0] as any).MARCA : 'n/a',
            uniqueMarcas: [...new Set(fetched.map(p => (p as any).marca ?? (p as any).MARCA ?? '').filter(Boolean))],
          });
        } else {
          const effectiveCategory = selectedCategory ?? categoryNameHeader;
          const response = await ProductsService.catalogProductsGet(
            user.clientCode,
            PER_PAGE,
            page,
            effectiveCategory,
            selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
            searchQuery || undefined,
            selectedSort ?? undefined,
            categoryIdsHeader,
            selectedPackaging ?? undefined,
            false,
          );
          fetched = (response.data?.products ?? []) as IProduct[];
          moreAvailable = fetched.length === PER_PAGE;

          if (!append && response?.data?.packagingFilter?.length) {
            setPackagingFilter(response.data.packagingFilter);
          }
        }

        const currentBag = bagProductsRef.current;
        fetched.forEach(product => {
          const inBag = currentBag.find(p => p.sku === product.sku);
          if (inBag) {
            product.quantity = inBag.quantity;
            const {price, discountedPrice} = calculatePriceBasedOnQuantity(
              product,
              inBag.quantity,
            );
            product.preco = price;
            (product as any).discountedPrice = discountedPrice;
          }
        });

        setHasMore(moreAvailable);

        if (append) {
          setProducts(prev => {
            const skus = new Set(prev.map(p => p.sku));
            const merged = [...prev, ...fetched.filter(p => !skus.has(p.sku))];
            // Refresh carousel brands from the full merged list on first append
            buildCarouselBrands(merged, allBrands);
            return merged;
          });
        } else {
          setProducts(fetched);
          // On first load with no brand filter, derive carousel brands from full set
          if (isPersonalCarousel && selectedBrands.length === 0) {
            buildCarouselBrands(fetched, allBrands);
          }
        }
      } catch (error) {
        console.error('CatalogBrowseScreen fetchProducts error:', error);
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [
      user?.clientCode,
      carouselId,
      isSuggested,
      selectedBrands,
      selectedCategory,
      searchQuery,
      selectedSort,
      selectedPackaging,
      categoryIdsHeader,
      categoryNameHeader,
      isPersonalCarousel,
      allBrands,
      buildCarouselBrands,
    ],
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.clientCode,
    carouselId,
    selectedBrands,
    selectedCategory,
    searchQuery,
    selectedSort,
    selectedPackaging,
    categoryNameHeader,
  ]);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const next = currentPage + 1;
    setCurrentPage(next);
    fetchProducts(next, true);
  }, [loading, loadingMore, hasMore, currentPage, fetchProducts]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      setCurrentPage(1);
      await fetchProducts(1, false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchProducts]);

  const handleAddBag = (product: IProduct, quantity: number) => {
    const cartKey = (product as any).is_promotion ? `${product.sku}_promo` : product.sku;
    const {products: currentProducts} = useBagStore.getState();
    if (currentProducts.find(p => (p.cartKey ?? p.sku) === cartKey)) {
      updateProductQuantity(cartKey, quantity);
    } else if (quantity > 0) {
      addProduct({...product, quantity, cartKey});
    }
  };

  const activeFiltersCount = [
    selectedBrands.length > 0 ? selectedBrands : null,
    lockedCategory ? null : selectedCategory,
    selectedSort,
    selectedPackaging,
  ].filter(Boolean).length;

  const renderItem = ({item}: {item: IProduct}) => (
    <ProductList
      clientCode={user?.clientCode}
      item={item}
      image={item.image}
      title={item.descricao}
      description={item.descricao + ' ' + item.complemento.replace('- R$', '')}
      AddBag={(quantity: number) => handleAddBag(item, quantity)}
      onPress={() =>
        navigation.navigate(AppRoutes.PRODUCT_DETAILS as never, {item} as never)
      }
    />
  );

  const renderFooter = () =>
    loadingMore ? (
      <View style={{paddingVertical: 10}}>
        <SkeletonLoader tiny />
      </View>
    ) : null;

  const renderListHeader = () => {
    if (isPersonalCarousel) {
      // Show brands extracted from the actual products of this carousel
      if (carouselBrands.length === 0) return null;
      return (
        <BrandPills
          brands={carouselBrands}
          selectedBrands={selectedBrands}
          onSelect={setSelectedBrands}
        />
      );
    }
    return (
      <>
        {lockedCategory ? (
          <BrandPills
            brands={visibleBrands}
            selectedBrands={selectedBrands}
            onSelect={setSelectedBrands}
          />
        ) : (
          <>
            <CategoryPills
              categories={categories}
              selectedCategory={selectedCategory}
              onSelect={cat => {
                setSelectedCategory(cat);
                setSelectedBrands([]);
              }}
            />
            {visibleBrands.length > 0 && (
              <BrandPills
                brands={visibleBrands}
                selectedBrands={selectedBrands}
                onSelect={setSelectedBrands}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Text style={styles.backText}>{'<'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>
        {isPersonalCarousel ? (
          <View style={styles.filterButton} />
        ) : (
          <TouchableOpacity onPress={() => setFilterModal(true)} style={styles.filterButton}>
            <Text style={styles.filterButtonText}>
              {'Filtrar' + (activeFiltersCount > 0 ? ` (${activeFiltersCount})` : '')}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {!isPersonalCarousel && (
        <View style={styles.searchBar}>
          <HomeInput
            value={searchInput}
            onPress={() => setSearchQuery(searchInput.trim())}
            onSubmitEditing={() => setSearchQuery(searchInput.trim())}
            placeholder="O que você procura?"
            onChangeText={handleSearchInput}
            returnKeyType="search"
            showOkButton
          />
        </View>
      )}

      {loading ? (
        <SkeletonLoader />
      ) : (
        <FlatList
          key={`catalog-browse-${numColumns}`}
          data={products}
          renderItem={renderItem}
          keyExtractor={(item, index) => 'browse_' + item.sku + index}
          numColumns={numColumns}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.1}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
          ListHeaderComponent={renderListHeader}
          ListFooterComponent={renderFooter}
          ListEmptyComponent={
            <View style={{marginVertical: 50}}>
              <NotFound
                description="Nenhum produto encontrado"
                title="Ops..."
              />
            </View>
          }
        />
      )}

      {!isPersonalCarousel && <ModalFilter
        visible={filterModal}
        onRequestClose={() => setFilterModal(false)}
        currentSort={selectedSort ?? ''}
        currentCategory={selectedCategory ?? ''}
        currentBrands={selectedBrands}
        currentNews={false}
        currentCombo={false}
        lockedCategory={lockedCategory}
        brandList={visibleBrands}
        returnFilter={f => {
          if (!lockedCategory) {
            setSelectedCategory(f.category ?? null);
          }
          setSelectedBrands(f.brands ?? []);
          setSelectedSort(f.sort ?? null);
          setSelectedPackaging(f.packaging ?? null);
          setFilterModal(false);
        }}
        cleanFilter={() => {
          setSelectedBrands([]);
          if (!lockedCategory) {
            setSelectedCategory(null);
          }
          setSelectedSort(null);
          setSelectedPackaging(null);
          setFilterModal(false);
        }}
        hasBrand={!lockedCategory}
        hasCategory={!lockedCategory}
        hasOrder
        hasSize
        hasPackaging
        packagingList={packagingFilter}
      />}
      <BagModal />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {flex: 1, backgroundColor: '#FFFFFF'},
  header: {
    backgroundColor: '#FF8C00',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  backButton: {padding: 4},
  backText: {fontSize: 20, fontWeight: 'bold', color: '#FFFFFF'},
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 1,
    marginHorizontal: 8,
  },
  filterButton: {padding: 4},
  filterButtonText: {fontSize: 14, color: '#FFFFFF', fontWeight: '600'},
  searchBar: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  listContent: {paddingBottom: 100, flexGrow: 1, paddingHorizontal: 8, paddingTop: 8},
});

export default CatalogBrowseScreen;
